/** @jsxImportSource preact */
import { useState, useEffect } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'
import { AirlineDirectFields, emptyAirlineDirectForm, type AirlineDirectFormState } from './AirlineDirectFields'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

// Mirrors PCC_CURRENCIES in create-booking's fulfil, which exchangeRates is keyed by.
const PCC_OPTIONS = ['W8QK', '4WBJ', 'IG5K', '1AYL', 'W3OM'] as const
type KnownPcc = (typeof PCC_OPTIONS)[number]

interface FulfilmentItem {
  id?: string
  bookingReference?: string
  workflowId: string
  type?: string
  triggerPath?: string
  reason?: string
  pnr?: string
  pcc?: string
  offeredPrice?: { amount: number; currencyCode: string }
  bookedPrice?: { amount: number; currencyCode: string }
  exchangeRates?: Partial<Record<KnownPcc, { currency: string; rate: number }>>
  ticketedOutsideAllowed?: boolean
}

type ReviewAction = 'approve' | 'decline' | 'ticketed-outside'

interface ActionDetails {
  label: string
  help: string
  confirmTitle: string
  confirmDetail: string
}

const ACTIONS: Record<ReviewAction, ActionDetails> = {
  approve: {
    label: 'Approve',
    help: 'The system takes the PNR and sends it for ticketing itself. Use when the PNR is held and not yet ticketed.',
    confirmTitle: 'Are you sure you want to approve this booking?',
    confirmDetail: 'This action will finalise this booking and send for ticketing.',
  },
  decline: {
    label: 'Decline',
    help: 'Cancels the booking: the deal moves to cancelled, the PNR is cancelled and the payment plan is suspended. Refund the customer afterwards.',
    confirmTitle: 'Are you sure you want to decline this booking?',
    confirmDetail: 'This action will finalise the booking so you can proceed with refunding the customer.',
  },
  'ticketed-outside': {
    label: 'Ticketed outside Ops App',
    help: 'Closes the booking without ticketing or cancelling anything. Use only when it was already booked and ticketed outside the Ops App, for example straight in Sabre or by the consolidator.',
    confirmTitle: 'Has this booking already been ticketed outside the Ops App?',
    confirmDetail: 'This action will close the booking. Nothing is sent for ticketing and nothing is cancelled.',
  },
}

const ACTION_ORDER: ReviewAction[] = ['approve', 'decline', 'ticketed-outside']

function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex group ml-1">
      <span
        tabIndex={0}
        aria-label={text}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-400 text-[10px] font-semibold text-gray-500 cursor-help focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        ?
      </span>
      <span
        role="tooltip"
        className="pointer-events-none invisible absolute left-0 top-full z-10 mt-1 w-64 max-w-[80vw] rounded-md bg-gray-800 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:visible group-focus-within:visible"
      >
        {text}
      </span>
    </span>
  )
}

function computeRevenue(dealValue: number | undefined, pnrValue: number | undefined): number | null {
  if (dealValue == null || pnrValue == null) return null
  return dealValue - pnrValue
}

function computeMarginPA(dealValue: number | undefined, pnrValue: number | undefined): number | null {
  const revenue = computeRevenue(dealValue, pnrValue)
  if (revenue === null || pnrValue == null || pnrValue === 0) return null
  return revenue / pnrValue
}

function computeMarginRTR(dealValue: number | undefined, pnrValue: number | undefined): number | null {
  const revenue = computeRevenue(dealValue, pnrValue)
  if (revenue === null || dealValue == null || dealValue === 0) return null
  return revenue / dealValue
}

// No rate for the office means no margin: the deal is in the customer's currency and the PNR
// value in the office's, and comparing them unconverted quietly invents a margin.
function computeMarginsForFulfilment(fulfilment: FulfilmentItem): { pa: number | null; rtr: number | null } {
  const { offeredPrice, bookedPrice, pcc, exchangeRates } = fulfilment
  if (offeredPrice == null || bookedPrice == null) return { pa: null, rtr: null }
  const rate = pcc ? exchangeRates?.[pcc as KnownPcc]?.rate : undefined
  if (rate == null) return { pa: null, rtr: null }

  const dealInPccCurrency = (offeredPrice.amount / 100) * rate
  const pnr_value = bookedPrice.amount / 100
  return {
    pa: computeMarginPA(dealInPccCurrency, pnr_value),
    rtr: computeMarginRTR(dealInPccCurrency, pnr_value),
  }
}

function formatMargin(margin: number | null): string {
  if (margin === null) return '-'
  return `${(margin * 100).toFixed(2)}%`
}

const TEAM_CREATE_NOTE_TEMPLATE = `Trigger: Segments Wait lists
Financial Compensation: $145
Reason: Ticket actioned at 36HR, past acceptable glitch period
Escalated for approval: Yes, to Alex`

export function Create() {
  const [bookingId, setBookingId] = useState('')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  const [fulfilments, setFulfilments] = useState<FulfilmentItem[]>([])
  const [selectedFulfilmentIndex, setSelectedFulfilmentIndex] = useState<number>(-1)
  const [fetchingFulfilments, setFetchingFulfilments] = useState(false)
  const [action, setAction] = useState<ReviewAction>('approve')
  const [note, setNote] = useState('')
  const [confirmedFulfilPrice, setConfirmedFulfilPrice] = useState('')
  const [newDealValue, setNewDealValue] = useState('')
  const [pnr, setPnr] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [ticketedOutsideConfirmed, setTicketedOutsideConfirmed] = useState(false)

  const [overridePnr, setOverridePnr] = useState(false)
  const [overrideDealValue, setOverrideDealValue] = useState(false)
  const [overridePnrValue, setOverridePnrValue] = useState(false)
  const [overridePcc, setOverridePcc] = useState(false)
  const [overridePccValue, setOverridePccValue] = useState('')

  const [airlineDirectForm, setAirlineDirectForm] = useState<AirlineDirectFormState>(emptyAirlineDirectForm())

  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  useEffect(() => {
    if (bookingId.trim()) {
      const timeoutId = setTimeout(() => {
        fetchFulfilmentsByBookingId(bookingId.trim())
      }, 800)
      return () => clearTimeout(timeoutId)
    } else {
      setFulfilments([])
      resetSelection()
    }
  }, [bookingId])

  const selectedFulfilment = selectedFulfilmentIndex >= 0 ? fulfilments[selectedFulfilmentIndex] : null
  const hasPnr = selectedFulfilment?.pnr != null && selectedFulfilment.pnr.trim() !== ''
  const requirePnr = !hasPnr
  const hasDealValue = selectedFulfilment?.offeredPrice != null
  const hasPnrOriginalValue = selectedFulfilment?.bookedPrice != null
  const hasPcc = selectedFulfilment?.pcc != null && selectedFulfilment.pcc.trim() !== ''
  const isAirlineDirectFulfilment = selectedFulfilment?.type === 'airline-direct-review'
  const canCloseTicketedOutside =
    selectedFulfilment != null && !isAirlineDirectFulfilment && selectedFulfilment.ticketedOutsideAllowed === true

  const updateAirlineDirectForm = (patch: Partial<AirlineDirectFormState>) =>
    setAirlineDirectForm(prev => ({ ...prev, ...patch }))

  useEffect(() => {
    if (selectedFulfilment) {
      if (hasPnr && !overridePnr) {
        setPnr(selectedFulfilment.pnr!)
      } else if (!hasPnr) {
        setPnr('')
        setOverridePnr(false)
      }
      if (hasDealValue && !overrideDealValue) {
        setNewDealValue(String(selectedFulfilment.offeredPrice!.amount / 100))
      } else if (!hasDealValue) {
        setNewDealValue('')
        setOverrideDealValue(false)
      }
      if (hasPnrOriginalValue && !overridePnrValue) {
        // What the PNR was booked at, in the currency it was booked in — no rate applied. The
        // rate converts the customer's deal value into this currency, not this value into
        // another one; multiplying here converted an already-PCC-currency amount a second time.
        setConfirmedFulfilPrice(String(selectedFulfilment.bookedPrice!.amount / 100))
      } else if (!hasPnrOriginalValue) {
        setConfirmedFulfilPrice('')
        setOverridePnrValue(false)
      }
      if (hasPcc && !overridePcc) {
        setOverridePccValue(selectedFulfilment.pcc!)
      } else if (!hasPcc) {
        setOverridePccValue('')
        setOverridePcc(false)
      }
      if (selectedFulfilment.type === 'airline-direct-review') {
        setAirlineDirectForm(emptyAirlineDirectForm())
      }
      if (action === 'ticketed-outside' && selectedFulfilment.ticketedOutsideAllowed !== true) {
        setAction('approve')
      }
    }
  }, [selectedFulfilmentIndex])

  const effectiveDealValue = newDealValue.trim() !== '' ? Number(newDealValue) : undefined

  const effectivePcc = (overridePcc ? overridePccValue : selectedFulfilment?.pcc) as KnownPcc | undefined
  const effectivePccEntry =
    effectivePcc && selectedFulfilment?.exchangeRates ? selectedFulfilment.exchangeRates[effectivePcc] ?? null : null

  // The office's own currency; without a rate we don't know it, so say nothing rather than
  // label the value with the customer's currency.
  const pnrDisplayCurrency = effectivePccEntry?.currency ?? selectedFulfilment?.bookedPrice?.currencyCode
  const pnrDisplayValue = confirmedFulfilPrice

  const effectivePnrValueInPccCurrency =
    confirmedFulfilPrice.trim() !== '' && !Number.isNaN(Number(confirmedFulfilPrice))
      ? Number(confirmedFulfilPrice)
      : undefined

  const safeEffectiveDealValue = Number.isNaN(effectiveDealValue) ? undefined : effectiveDealValue
  // Only comparable once converted: no rate, no revenue or margin (rendered as "-").
  const dealInPccCurrency =
    safeEffectiveDealValue != null && effectivePccEntry != null
      ? safeEffectiveDealValue * effectivePccEntry.rate
      : undefined
  const inputRevenue = computeRevenue(dealInPccCurrency, effectivePnrValueInPccCurrency)
  const inputMarginPA = computeMarginPA(dealInPccCurrency, effectivePnrValueInPccCurrency)
  const inputMarginRTR = computeMarginRTR(dealInPccCurrency, effectivePnrValueInPccCurrency)

  const fetchFulfilmentsByBookingId = async (id: string) => {
    if (!id) return

    setFetchingFulfilments(true)
    setErrorMessage('')
    resetSelection()

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${id}/fulfilments/team-create`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch fulfilments')
      }

      const data: FulfilmentItem[] = await response.json()
      const needTeamCreate = data
      setFulfilments(needTeamCreate)

      if (needTeamCreate.length === 0) {
        setErrorMessage(
          data.length === 0
            ? 'No fulfilments found for this booking ID'
            : 'No fulfilments awaiting team create review for this booking.',
        )
      }
    } catch (error) {
      console.error('Error fetching fulfilments:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch fulfilments')
      setFulfilments([])
    } finally {
      setFetchingFulfilments(false)
    }
  }

  const resetSelection = () => {
    setSelectedFulfilmentIndex(-1)
    setPnr('')
    setConfirmedFulfilPrice('')
    setNewDealValue('')
    setOverridePnr(false)
    setOverrideDealValue(false)
    setOverridePnrValue(false)
    setOverridePcc(false)
    setOverridePccValue('')
    setAirlineDirectForm(emptyAirlineDirectForm())
  }

  const handleRefreshFulfilments = () => {
    if (bookingId.trim()) {
      fetchFulfilmentsByBookingId(bookingId.trim())
    }
  }

  const handleFulfilmentSelectionChange = (index: number) => {
    setSelectedFulfilmentIndex(index)
    setOverridePnr(false)
    setOverrideDealValue(false)
    setOverridePnrValue(false)
    setOverridePcc(false)
  }

  const resetForm = () => {
    setFormStatus('idle')
    setErrorMessage('')
    setAction('approve')
    setShowConfirmation(false)
    setSelectedFulfilmentIndex(-1)
    setNote('')
    setConfirmedFulfilPrice('')
    setNewDealValue('')
    setPnr('')
    setOverridePnr(false)
    setOverrideDealValue(false)
    setOverridePnrValue(false)
    setOverridePcc(false)
    setOverridePccValue('')
    setAirlineDirectForm(emptyAirlineDirectForm())
  }

  const handleFormSubmit = async (e: Event) => {
    e.preventDefault()

    if (!bookingId.trim()) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    if (!note.trim()) {
      setErrorMessage('Note is required')
      setFormStatus('error')
      return
    }

    if (action === 'approve') {
      if (isAirlineDirectFulfilment) {
        const missingField =
          !airlineDirectForm.airlineReference.trim() ||
          airlineDirectForm.eticketUploaded === '' ||
          !airlineDirectForm.ticketingDate ||
          airlineDirectForm.netFareCost.trim() === '' ||
          airlineDirectForm.grossFareCost.trim() === ''
        if (missingField) {
          setErrorMessage('All airline direct ticketing fields are required to approve')
          setFormStatus('error')
          return
        }
        const netFareNum = Number(airlineDirectForm.netFareCost)
        const grossFareNum = Number(airlineDirectForm.grossFareCost)
        if (Number.isNaN(netFareNum) || Number.isNaN(grossFareNum) || netFareNum < 0 || grossFareNum < 0) {
          setErrorMessage('Net and gross fare costs must be valid non-negative numbers')
          setFormStatus('error')
          return
        }
      } else if ((requirePnr || overridePnr) && !pnr.trim()) {
        setErrorMessage('PNR is required')
        setFormStatus('error')
        return
      }
    }

    if (selectedFulfilmentIndex === -1 || !selectedFulfilment) {
      setErrorMessage('Please select a fulfilment from the table')
      setFormStatus('error')
      return
    }

    if (!selectedFulfilment.workflowId) {
      setErrorMessage('Selected fulfilment does not have a workflow ID')
      setFormStatus('error')
      return
    }

    if (!showConfirmation) {
      setTicketedOutsideConfirmed(false)
      setShowConfirmation(true)
      return
    }

    if (action === 'ticketed-outside' && !ticketedOutsideConfirmed) {
      return
    }

    setFormStatus('loading')
    setErrorMessage('')

    try {
      const username = user && (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email)
      const submittedNote = username ? `${note.trim()} - submitted by ${username}` : note.trim()

      const endpoint = isAirlineDirectFulfilment
        ? `${config.apiBaseUrl}/bookings/airline-direct/finalise`
        : `${config.apiBaseUrl}/bookings/team-create/finalise`
      const requestBody: Record<string, unknown> = {
        workflowId: selectedFulfilment.workflowId,
        passed: action !== 'decline',
        reason: submittedNote,
      }

      if (action === 'ticketed-outside') {
        requestBody.ticketed_outside_ops_app = true
      }

      if (action === 'approve') {
        if (isAirlineDirectFulfilment) {
          requestBody.airlineReference = airlineDirectForm.airlineReference.trim()
          requestBody.eticketUploaded = airlineDirectForm.eticketUploaded
          requestBody.ticketingDate = airlineDirectForm.ticketingDate
          requestBody.netFareCost = Number(airlineDirectForm.netFareCost)
          requestBody.grossFareCost = Number(airlineDirectForm.grossFareCost)
        } else {
          if (overridePnrValue || !hasPnrOriginalValue) {
            const priceNum = Number(confirmedFulfilPrice)
            if (!Number.isNaN(priceNum) && confirmedFulfilPrice.trim() !== '') {
              requestBody.pnr_value = priceNum
            }
          }
          if (overrideDealValue || !hasDealValue) {
            const dealNum = Number(newDealValue)
            if (!Number.isNaN(dealNum) && newDealValue.trim() !== '') {
              requestBody.offered_price = dealNum
            }
          }
          if (overridePnr || requirePnr) {
            if (pnr.trim() !== '') requestBody.pnr = pnr.trim()
          }
          if ((overridePcc || !hasPcc) && overridePccValue.trim() !== '') {
            requestBody.pcc = overridePccValue.trim()
          }
        }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.error ||
            (isAirlineDirectFulfilment
              ? 'Failed to finalise airline direct review'
              : 'Failed to finalise team create review'),
        )
      }

      setFormStatus('success')
      setShowConfirmation(false)
      setTimeout(() => {
        setFormStatus('idle')
        handleRefreshFulfilments()
      }, 3000)
    } catch (error) {
      const reviewKind = isAirlineDirectFulfilment ? 'airline direct' : 'team create'
      console.error(`Error submitting ${reviewKind} review:`, error)
      setErrorMessage(error instanceof Error ? error.message : `Failed to submit ${reviewKind} review`)
      setFormStatus('error')
      setShowConfirmation(false)
    }
  }

  const handleCancelConfirmation = () => {
    setShowConfirmation(false)
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Team Create Review</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Finalise Team Create Review</h1>
      <p className="text-sm text-gray-600 mb-6">
        Several bookings already ticketed outside the Ops App?{' '}
        <a
          href={config.routePrefix + '/create/bulk-ticketed-outside'}
          className="text-indigo-600 hover:text-indigo-800 underline"
        >
          Close them in bulk
        </a>
      </p>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="bookingId"
            type="text"
            value={bookingId}
            onChange={e => setBookingId((e.target as HTMLInputElement).value)}
            className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
            placeholder="Enter Booking ID"
            required
          />
        </div>
        {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
        {bookingId && isFrontApp && (
          <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-center mb-3">
          <h2 className="text-lg font-semibold text-gray-800 mr-2">Ticketing (Awaiting Team Create)</h2>
          <button
            type="button"
            onClick={handleRefreshFulfilments}
            className="text-indigo-600 hover:text-indigo-800 focus:outline-none"
            title="Refresh Fulfilments"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {fetchingFulfilments ? (
          <div className="p-4 bg-gray-50 rounded-md flex justify-center items-center">
            <svg
              className="animate-spin h-6 w-6 text-indigo-600 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading fulfilments...</span>
          </div>
        ) : (
          <>
            {fulfilments.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-md text-center">
                {bookingId
                  ? 'No ticketing items awaiting team create review for this booking.'
                  : 'Enter a booking ID to view fulfilments.'}
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      ></th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Reason
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        PNR
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Deal Value
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        PNR Value
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Margin
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fulfilments.map((fulfilment, index) => {
                      const { pa: marginPA, rtr: marginRTR } = computeMarginsForFulfilment(fulfilment)
                      return (
                        <tr
                          key={index}
                          className={selectedFulfilmentIndex === index ? 'bg-indigo-50' : ''}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="radio"
                              name="fulfilmentSelection"
                              value={index}
                              checked={selectedFulfilmentIndex === index}
                              onChange={() => handleFulfilmentSelectionChange(index)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">{fulfilment.reason || '-'}</td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {fulfilment.pnr ?? ''} ({fulfilment.pcc ?? ''})
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {fulfilment.offeredPrice != null
                              ? `${fulfilment.offeredPrice.currencyCode} ${(fulfilment.offeredPrice.amount / 100).toFixed(2)}`
                              : ''}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {fulfilment.bookedPrice != null
                              ? `${fulfilment.bookedPrice.currencyCode} ${(fulfilment.bookedPrice.amount / 100).toFixed(2)}`
                              : ''}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            <div className="flex flex-col gap-1">
                              {marginPA !== null && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-400 w-6">PA</span>
                                  <span className="text-xs font-semibold text-gray-600">{formatMargin(marginPA)}</span>
                                </div>
                              )}
                              {marginRTR !== null && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-400 w-6">RTR</span>
                                  <span className="text-xs font-semibold text-gray-600">{formatMargin(marginRTR)}</span>
                                </div>
                              )}
                              {marginPA === null && marginRTR === null && (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <form
        onSubmit={handleFormSubmit}
        className="space-y-4"
      >
        {selectedFulfilment && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              <strong>Selected:</strong> {selectedFulfilment.workflowId}
              {selectedFulfilment.offeredPrice?.currencyCode && (
                <span className="ml-2">({selectedFulfilment.offeredPrice.currencyCode})</span>
              )}
              {!isAirlineDirectFulfilment && requirePnr && !hasPnr && action === 'approve' && (
                <span className="block mt-1 text-amber-700">PNR input required (manual PNR).</span>
              )}
              {isAirlineDirectFulfilment && (
                <span className="block mt-1 text-amber-700">
                  Airline direct booking — record ticketing details to approve.
                </span>
              )}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Action <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6">
            {ACTION_ORDER.filter(value => canCloseTicketedOutside || value !== 'ticketed-outside').map(value => (
              <label
                key={value}
                className="inline-flex items-center"
              >
                <input
                  type="radio"
                  className="form-radio text-indigo-600"
                  name="action"
                  value={value}
                  checked={action === value}
                  onChange={() => setAction(value)}
                />
                <span className="ml-2">{ACTIONS[value].label}</span>
                <InfoTooltip text={ACTIONS[value].help} />
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-600">{ACTIONS[action].help}</p>
          {selectedFulfilment && !isAirlineDirectFulfilment && !canCloseTicketedOutside && (
            <p className="mt-1 text-xs text-gray-500">
              Ticketed outside Ops App is not available here: this booking entered the Create tab before the option
              existed. If it is already ticketed, ask tech in #tech-support to close it.
            </p>
          )}
        </div>

        {action === 'approve' &&
          (isAirlineDirectFulfilment ? (
            <AirlineDirectFields
              value={airlineDirectForm}
              currencyCode={selectedFulfilment?.offeredPrice?.currencyCode}
              onChange={updateAirlineDirectForm}
            />
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">PCC</label>
                  {hasPcc && (
                    <label className="inline-flex items-center text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overridePcc}
                        onChange={e => {
                          const checked = (e.target as HTMLInputElement).checked
                          setOverridePcc(checked)
                          if (!checked && selectedFulfilment?.pcc) {
                            setOverridePccValue(selectedFulfilment.pcc)
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-1"
                      />
                      Override
                    </label>
                  )}
                </div>
                {hasPcc && !overridePcc ? (
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                    {overridePccValue}
                  </div>
                ) : (
                  <select
                    value={overridePccValue}
                    onChange={e => setOverridePccValue((e.target as HTMLSelectElement).value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Select PCC</option>
                    {PCC_OPTIONS.map(pcc => (
                      <option
                        key={pcc}
                        value={pcc}
                      >
                        {pcc}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    PNR Value <span className="text-red-500">*</span>
                  </label>
                  {hasPnrOriginalValue && (
                    <label className="inline-flex items-center text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overridePnrValue}
                        onChange={e => {
                          const checked = (e.target as HTMLInputElement).checked
                          setOverridePnrValue(checked)
                          if (!checked && selectedFulfilment?.bookedPrice != null) {
                            setConfirmedFulfilPrice(String(selectedFulfilment.bookedPrice.amount / 100))
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-1"
                      />
                      Override
                    </label>
                  )}
                </div>
                {hasPnrOriginalValue && !overridePnrValue ? (
                  <div className="flex rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                    {pnrDisplayCurrency && (
                      <span className="px-3 py-2 bg-gray-100 text-gray-500 text-sm border-r border-gray-200">
                        {pnrDisplayCurrency}
                      </span>
                    )}
                    <span className="px-3 py-2 text-gray-700">{pnrDisplayValue}</span>
                  </div>
                ) : (
                  <div className="flex rounded-md shadow-sm">
                    {pnrDisplayCurrency && (
                      <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                        {pnrDisplayCurrency}
                      </span>
                    )}
                    <input
                      type="number"
                      step="any"
                      value={confirmedFulfilPrice}
                      onChange={e => setConfirmedFulfilPrice((e.target as HTMLInputElement).value)}
                      className={`flex-1 px-3 py-2 border border-gray-300 ${pnrDisplayCurrency ? 'rounded-r-md' : 'rounded-md'} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                      placeholder="e.g. 1234.56"
                      required
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Deal Value {!hasDealValue && '(optional)'}
                  </label>
                  {hasDealValue && (
                    <label className="inline-flex items-center text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overrideDealValue}
                        onChange={e => {
                          const checked = (e.target as HTMLInputElement).checked
                          setOverrideDealValue(checked)
                          if (!checked && selectedFulfilment?.offeredPrice != null) {
                            setNewDealValue(String(selectedFulfilment.offeredPrice.amount / 100))
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-1"
                      />
                      Override
                    </label>
                  )}
                </div>
                {hasDealValue && !overrideDealValue ? (
                  <div className="flex rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                    {selectedFulfilment?.offeredPrice?.currencyCode && (
                      <span className="px-3 py-2 bg-gray-100 text-gray-500 text-sm border-r border-gray-200">
                        {selectedFulfilment.offeredPrice.currencyCode}
                      </span>
                    )}
                    <span className="px-3 py-2 text-gray-700">{newDealValue}</span>
                  </div>
                ) : (
                  <div className="flex rounded-md shadow-sm">
                    {selectedFulfilment?.offeredPrice?.currencyCode && (
                      <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                        {selectedFulfilment.offeredPrice.currencyCode}
                      </span>
                    )}
                    <input
                      type="number"
                      step="any"
                      value={newDealValue}
                      onChange={e => setNewDealValue((e.target as HTMLInputElement).value)}
                      className={`flex-1 px-3 py-2 border border-gray-300 ${selectedFulfilment?.offeredPrice?.currencyCode ? 'rounded-r-md' : 'rounded-md'} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                      placeholder="e.g. 1200.00"
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    PNR {(requirePnr || (hasPnr && overridePnr)) && <span className="text-red-500">*</span>}
                  </label>
                  {hasPnr && (
                    <label className="inline-flex items-center text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overridePnr}
                        onChange={e => {
                          const checked = (e.target as HTMLInputElement).checked
                          setOverridePnr(checked)
                          if (!checked && selectedFulfilment?.pnr) {
                            setPnr(selectedFulfilment.pnr)
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-1"
                      />
                      Override
                    </label>
                  )}
                </div>
                {hasPnr && !overridePnr ? (
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                    {pnr}
                  </div>
                ) : requirePnr || overridePnr ? (
                  <input
                    type="text"
                    value={pnr}
                    onChange={e => setPnr((e.target as HTMLInputElement).value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter PNR reference"
                    required={requirePnr || overridePnr}
                  />
                ) : null}
              </div>

              {selectedFulfilment && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-1">
                  {inputRevenue === null ? (
                    <p className="text-sm text-gray-400">Enter both deal value and PNR value to calculate margins</p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 w-28">Margin on PA</span>
                        <span className="text-sm font-semibold text-gray-800">{formatMargin(inputMarginPA)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 w-28">Margin on RTR</span>
                        <span className="text-sm font-semibold text-gray-800">{formatMargin(inputMarginRTR)}</span>
                      </div>
                      <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                        <span className="text-xs text-gray-400 w-28">Deal</span>
                        <span className="text-xs text-gray-500">
                          {effectivePccEntry == null
                            ? `no exchange rate for ${effectivePcc ?? 'this PCC'} — margin unavailable`
                            : effectivePccEntry.rate !== 1
                              ? `${safeEffectiveDealValue?.toFixed(2)} ${selectedFulfilment.offeredPrice?.currencyCode ?? ''} × ${effectivePccEntry.rate} = ${dealInPccCurrency?.toFixed(2)} ${effectivePccEntry.currency}`
                              : `${dealInPccCurrency?.toFixed(2)} ${effectivePccEntry.currency}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-28">PNR</span>
                        <span className="text-xs text-gray-500">
                          {effectivePnrValueInPccCurrency?.toFixed(2)} {pnrDisplayCurrency ?? ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-28">Revenue</span>
                        <span className="text-xs text-gray-500">
                          {inputRevenue.toFixed(2)}{' '}
                          {pnrDisplayCurrency ?? selectedFulfilment.offeredPrice?.currencyCode ?? ''}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          ))}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note <span className="text-red-500">*</span>
          </label>
          <textarea
            value={note}
            onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            rows={8}
            placeholder={TEAM_CREATE_NOTE_TEMPLATE}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Use the suggested placeholder format. For negative margin bookings, include a clear `Reason:`.
          </p>
        </div>

        {showConfirmation && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-800 font-semibold mb-2">{ACTIONS[action].confirmTitle}</p>
            <p className="text-xs text-amber-700 mb-3">{ACTIONS[action].confirmDetail}</p>
            {action === 'ticketed-outside' && (
              <label className="flex items-start gap-2 mb-3 text-sm text-amber-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ticketedOutsideConfirmed}
                  onChange={e => setTicketedOutsideConfirmed((e.target as HTMLInputElement).checked)}
                  className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span>I have checked in Sabre that this booking is already ticketed.</span>
              </label>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={formStatus === 'loading' || (action === 'ticketed-outside' && !ticketedOutsideConfirmed)}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? 'Processing...' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={handleCancelConfirmation}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!showConfirmation && (
          <div className="pt-4">
            <button
              type="submit"
              disabled={formStatus === 'loading' || selectedFulfilmentIndex === -1}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Finalise Team Create Review'}
            </button>
          </div>
        )}
      </form>

      {formStatus === 'success' && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">Team create review finalised successfully!</p>
        </div>
      )}

      {formStatus === 'error' && errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="mt-6">
        <button
          type="button"
          onClick={resetForm}
          className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset Form
        </button>
      </div>
    </div>
  )
}
